import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, type Attachment, type ConversationDetail, type ConversationRow, type Message, type Operator, type TeamMember } from "../../api";
import { ImageViewer } from "../../components/Media";
import { useDialogs } from "../../components/Dialog";
import { SearchIcon } from "../../components/Icons";
import { dateTime, statusLabel, visitorLabel } from "../../lib/format";
import { useI18n, type Dict, type Lang } from "../../lib/i18n";
import { downloadCsv, fileStamp, type Cell } from "../../lib/csv";
import { mergeMessages } from "../../lib/thread";
import { ConversationRowButton } from "../inbox/ConversationList";
import { ConversationHeader } from "../inbox/ConversationPane";
import { Thread } from "../inbox/Thread";
import { ContextPanel } from "../inbox/ContextPanel";
import { keepEarlier } from "../inbox/InboxView";
import { askBan, askDeleteConversation, askPurge, askTag, askVisitorDetails } from "../inbox/asks";
import { apiErrorText } from "../../lib/apiError";

/**
 * Finished conversations: find one again by date, by who handled it, by how it
 * was rated, by site or tag, or by anything said in it. Opening one shows it
 * read-only; the header's "Reopen" moves it back to the inbox.
 */

export interface Filters {
  q: string;
  from: string;
  to: string;
  operatorId: string;
  rating: string;
  siteId: string;
  tag: string;
}

const EMPTY: Filters = { q: "", from: "", to: "", operatorId: "", rating: "", siteId: "", tag: "" };

/** How many conversations one export may hold; past this the dates should be narrowed instead. */
export const EXPORT_MAX = 5000;
const EXPORT_PAGE = 100;

/** The list request a set of filters stands for — shared by the screen and the export, so they cannot disagree. */
export function historyQuery(filters: Filters): Record<string, string | undefined> {
  return {
    status: "done",
    q: filters.q.trim() || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    operatorId: filters.operatorId || undefined,
    rating: filters.rating || undefined,
    siteId: filters.siteId || undefined,
    tag: filters.tag.trim() || undefined,
  };
}

/** The history as spreadsheet rows, header first. */
export function historyCsv(rows: ConversationRow[], t: Dict, lang: Lang): Cell[][] {
  const h = t.history.csv;
  return [
    [h.id, h.site, h.visitor, h.email, h.status, h.operator, h.rating, h.tags, h.started, h.closed],
    ...rows.map((row) => [
      row.id,
      row.siteName,
      visitorLabel(row, lang),
      row.visitorEmail,
      statusLabel(row.status, lang),
      row.assignee?.displayName ?? "",
      row.rating,
      row.tags.join(" "),
      dateTime(row.createdAt, lang),
      row.closedAt ? dateTime(row.closedAt, lang) : "",
    ]),
  ];
}

export function HistoryView({
  operator,
  selectedId,
  select,
}: {
  operator: Operator;
  selectedId: number | null;
  select: (id: number | null) => void;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.history;
  const dialogs = useDialogs();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [sites, setSites] = useState<Array<{ id: number; name: string }>>([]);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  const walkedBack = useRef<number | null>(null);

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      try {
        const res = await api.conversations({ ...historyQuery(filters), before, limit: 30 });
        setRows((current) => (before ? [...current, ...res.conversations] : res.conversations));
        setNextBefore(res.nextBefore);
      } catch {
        setToast(all.inbox.listFailed);
      } finally {
        setLoading(false);
      }
    },
    [filters, all],
  );

  // Typing in the search or the tag waits for a pause; the other filters load at once.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), filters.q || filters.tag ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, filters.q, filters.tag]);

  useEffect(() => {
    void api
      .team()
      .then((r) => setTeam(r.operators))
      .catch(() => undefined);
    void api
      .sites()
      .then((r) => setSites(r.sites.map((s) => ({ id: s.id, name: s.name }))))
      .catch(() => undefined);
  }, []);

  const loadDetail = useCallback(
    async (id: number) => {
      try {
        const res = await api.conversation(id);
        if (selectedRef.current !== id) return;
        setDetail(res);
        setMessages((current) => keepEarlier(current, res.messages, id));
        if (walkedBack.current !== id) setHasEarlier(res.hasEarlier ?? false);
      } catch {
        setToast(all.inbox.openFailed);
      }
    },
    [all],
  );

  useEffect(() => {
    setInfoOpen(false);
    setLoadingEarlier(false);
    walkedBack.current = null;
    if (selectedId) void loadDetail(selectedId);
    else {
      setDetail(null);
      setMessages([]);
      setHasEarlier(false);
    }
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const set = (key: keyof Filters, value: string): void => setFilters((f) => ({ ...f, [key]: value }));
  const open = selectedId && detail && detail.conversation.id === selectedId;

  async function run(action: () => Promise<unknown>, done?: string): Promise<void> {
    try {
      await action();
      if (done) setToast(done);
      if (selectedId) await loadDetail(selectedId);
      void load();
    } catch (err) {
      setToast(apiErrorText(err, all, all.common.actionFailed));
    }
  }

  /** See InboxView: an erasure closes the pane before reloading, never after. */
  async function erase<T>(action: () => Promise<T>, done: (result: T) => string): Promise<void> {
    try {
      const result = await action();
      select(null);
      setToast(done(result));
      void load();
    } catch (err) {
      setToast(apiErrorText(err, all, all.common.actionFailed));
    }
  }

  async function loadEarlier(): Promise<void> {
    const id = selectedId;
    const first = messages.find((m) => m.id > 0);
    if (!id || !first || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const res = await api.earlierMessages(id, first.id);
      if (selectedRef.current !== id) return;
      walkedBack.current = id;
      setMessages((current) => mergeMessages(current, res.messages));
      setHasEarlier(res.hasEarlier);
    } catch (err) {
      setToast(apiErrorText(err, all, all.common.actionFailed));
    } finally {
      setLoadingEarlier(false);
    }
  }

  /** Everything the current filters match, not only the pages already scrolled through. */
  async function exportCsv(): Promise<void> {
    setExporting(true);
    try {
      const collected: ConversationRow[] = [];
      let before: number | undefined;
      let more = true;
      while (more && collected.length < EXPORT_MAX) {
        const res = await api.conversations({ ...historyQuery(filters), before, limit: EXPORT_PAGE });
        collected.push(...res.conversations);
        more = res.nextBefore !== null;
        before = res.nextBefore ?? undefined;
      }
      const capped = more;
      const out = collected.slice(0, EXPORT_MAX);
      downloadCsv(`history-${fileStamp()}.csv`, historyCsv(out, all, lang));
      setToast(capped ? t.exportCapped(out.length) : t.exported(out.length));
    } catch {
      setToast(all.common.exportFailed);
    } finally {
      setExporting(false);
    }
  }

  const isAdmin = operator.role === "owner" || operator.role === "admin";
  const now = Date.now();
  const visitorName = detail
    ? visitorLabel(
        {
          visitorName: detail.visitor?.displayName ?? null,
          visitorEmail: detail.visitor?.email ?? null,
          visitorId: detail.conversation.visitorId,
        },
        lang,
      )
    : "";

  return (
    <div class={`inbox${selectedId ? " has-selection" : ""}`}>
      <section class="pane list-pane" aria-label={t.title}>
        <div class="list-head">
          <div class="list-title">
            <h1>{t.title}</h1>
            <button class="btn ghost sm" disabled={exporting || rows.length === 0} onClick={() => void exportCsv()}>
              {exporting ? all.common.exporting : all.common.exportCsv}
            </button>
          </div>
          <p class="hint" style={{ margin: "-4px 0 10px" }}>
            {t.intro}
          </p>
          <div class="search">
            <SearchIcon />
            <input type="search" placeholder={t.search} aria-label={t.search} value={filters.q} onInput={(e) => set("q", e.currentTarget.value)} />
          </div>
          <div class="filters-grid">
            <label>
              {t.from}
              <input type="date" value={filters.from} onInput={(e) => set("from", e.currentTarget.value)} />
            </label>
            <label>
              {t.to}
              <input type="date" value={filters.to} onInput={(e) => set("to", e.currentTarget.value)} />
            </label>
            <label>
              {t.operator}
              <select value={filters.operatorId} onChange={(e) => set("operatorId", e.currentTarget.value)}>
                <option value="">{t.anyone}</option>
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t.rating}
              <select value={filters.rating} onChange={(e) => set("rating", e.currentTarget.value)}>
                <option value="">{t.anyRating}</option>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {t.stars(n)}
                  </option>
                ))}
                <option value="0">{t.noRating}</option>
              </select>
            </label>
            {sites.length > 1 ? (
              <label>
                {all.common.site}
                <select value={filters.siteId} onChange={(e) => set("siteId", e.currentTarget.value)}>
                  <option value="">{all.common.allSites}</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              {all.common.tag}
              <input
                type="text"
                maxLength={40}
                placeholder={t.tagPlaceholder}
                value={filters.tag}
                onInput={(e) => set("tag", e.currentTarget.value)}
              />
            </label>
          </div>
        </div>

        <div class="rows">
          {rows.length === 0 ? <div class="empty">{loading ? all.common.loading : t.empty}</div> : null}
          {rows.map((row) => (
            <ConversationRowButton
              key={row.id}
              row={{ ...row, waitingSince: null, unread: 0 }}
              selected={row.id === selectedId}
              onSelect={() => select(row.id)}
              now={now}
              meta={row.closedAt ? dateTime(row.closedAt, lang) : undefined}
            />
          ))}
          {nextBefore ? (
            <div class="load-more">
              <button class="btn sm" disabled={loading} onClick={() => void load(nextBefore)}>
                {t.more}
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <section class="pane conv-pane" aria-label={all.inbox.conversationLabel}>
        {open ? (
          <>
            <ConversationHeader
              detail={detail}
              team={team}
              meId={operator.id}
              isAdmin={isAdmin}
              onDelete={() => {
                void askDeleteConversation(dialogs, all).then((sure) => {
                  if (sure) void erase(() => api.deleteConversation(selectedId), () => all.inbox.deletedToast);
                });
              }}
              onBack={() => select(null)}
              onInfo={() => setInfoOpen(true)}
              onAssign={(id) => void run(() => api.assign(selectedId, id))}
              onStatus={(status) =>
                void run(
                  () => api.setStatus(selectedId, status),
                  status === "resolved" ? all.inbox.resolvedToast : all.inbox.reopenedToast,
                )
              }
              onTags={(tags) => void run(() => api.setTags(selectedId, tags))}
              onAddTag={() => {
                void askTag(dialogs, all, detail.conversation.tags).then((added) => {
                  if (added) void run(() => api.setTags(selectedId, [...detail.conversation.tags, added]));
                });
              }}
              onTranscript={() => void run(() => api.sendTranscript(selectedId), all.inbox.transcriptSent(detail.visitor?.email ?? ""))}
            />
            <Thread
              messages={messages}
              visitorName={visitorName}
              onOpenImage={setViewing}
              hasEarlier={hasEarlier}
              loadingEarlier={loadingEarlier}
              onLoadEarlier={() => void loadEarlier()}
            />
            <div class="readonly-bar">{all.conv.readOnly}</div>
          </>
        ) : selectedId ? (
          <div class="empty">{all.common.loading}</div>
        ) : (
          <div class="empty">
            <div>
              <strong>{all.inbox.pickTitle}</strong>
            </div>
          </div>
        )}
      </section>

      {open ? (
        <ContextPanel
          detail={detail}
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          onOpenPast={(id) => select(id)}
          isAdmin={isAdmin}
          onBan={() => {
            const visitor = detail.visitor;
            if (!visitor) return;
            void askBan(dialogs, all).then((reason) => {
              if (reason !== null) void run(() => api.banVisitor(visitor.id, reason), all.inbox.bannedToast);
            });
          }}
          onUnban={() => {
            if (!detail.visitor) return;
            void run(() => api.unbanVisitor(detail.visitor!.id), all.inbox.unbannedToast);
          }}
          onPurge={() => {
            const visitor = detail.visitor;
            if (!visitor) return;
            void askPurge(dialogs, all, visitorName).then((sure) => {
              if (sure) void erase(() => api.purgeVisitor(visitor.id), (res) => all.inbox.purgedToast(res.conversations));
            });
          }}
          onEditVisitor={() => {
            const visitor = detail.visitor;
            if (!visitor) return;
            void askVisitorDetails(dialogs, all, visitor).then((next) => {
              if (next) void run(() => api.updateVisitor(visitor.id, next), all.inbox.visitorSaved);
            });
          }}
        />
      ) : (
        <aside class="pane context" aria-hidden="true" />
      )}

      {viewing ? <ImageViewer item={viewing} onClose={() => setViewing(null)} /> : null}
      {dialogs.dialog}
      {toast ? (
        <div class="alert toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
